// src/pages/leads/ViewLeads.tsx
import { useMemo, useState } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiPlus, FiSearch, FiPrinter, FiDownload } from "react-icons/fi";

import {
  useLeadsPage,
  type LeadStatus,
  type LeadSource,
  type Priority,
  type LeadRow,
} from "./useLeadsPage";

import LeadTable from "@/components/leads/LeadTable";
import TabBar from "@/components/shared/ui/TabBar";

/* ------------------------------ Options / UI ----------------------------- */

const SOURCE_OPTIONS: LeadSource[] = [
  "Walk-in",
  "Referral",
  "Online",
  "Advertisement",
  "Social Media",
  "Partner",
  "Phone Inquiry",
  "Email Campaign",
  "Other",
];

const PRIORITY_OPTIONS: Priority[] = ["Low", "Medium", "High", "Urgent"];

// ✅ Status tabs config
const STATUS_TABS: { key: LeadStatus; label: string }[] = [
  { key: "Pending", label: "Pending" },
  { key: "Completed", label: "Completed" },
  { key: "Cancelled", label: "Cancelled" },
];

/* --------------------------------- Utils --------------------------------- */
function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((p, idx) => {
    const href = "/" + parts.slice(0, idx + 1).join("/");
    const label = p
      .split("-")
      .map((s) => s[0]?.toUpperCase() + s.slice(1))
      .join(" ");
    return { href, label };
  });
  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
        </li>
        {crumbs.map((c, i) => (
          <li key={c.href} className="flex items-center gap-1">
            <span>/</span>
            {i < crumbs.length - 1 ? (
              <Link to={c.href} className="hover:text-gray-700">
                {c.label}
              </Link>
            ) : (
              <span className="text-gray-700">{c.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function downloadCsv(filename: string, rows: Record<string, any>[]) {
  // Spreadsheets treat cells starting with = + - @ as numbers/formulas: they strip
  // the "+" from phone numbers and can execute injected formulas from lead input.
  const toSafeCell = (v: any) => {
    const s = String(v ?? "");
    if (/^\+[\d\s()-]+$/.test(s)) return `="${s}"`; // phone: keep "+" as literal text
    if (/^[=+\-@\t\r]/.test(s)) return `'${s}`;
    return s;
  };

  const esc = (v: any) => {
    const s = toSafeCell(v);
    // Quote if contains comma/quote/newline
    if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const headers = Object.keys(rows[0] ?? {});
  const lines = [
    headers.map(esc).join(","),
    ...rows.map((r) => headers.map((h) => esc(r[h])).join(",")),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

type ExportableLeadColumn =
  | "id"
  | "firstName"
  | "lastName"
  | "phone"
  | "email"
  | "date"
  | "products"
  | "status"
  | "employee"
  | "source"
  | "priority";

const LEAD_COLUMNS: { key: ExportableLeadColumn; label: string }[] = [
  { key: "id", label: "Lead ID" },
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "date", label: "Date" },
  { key: "products", label: "Products" },
  { key: "status", label: "Status" },
  { key: "employee", label: "Lead Owner" },
  { key: "source", label: "Source" },
  { key: "priority", label: "Priority" },
];

function getLeadValue(r: LeadRow, key: ExportableLeadColumn): string | number {
  switch (key) {
    case "id":
      return r.id;
    case "firstName":
      return r.firstName;
    case "lastName":
      return r.lastName;
    case "phone":
      return r.phone;
    case "email":
      return r.email;
    case "date":
      return r.date;
    case "products":
      return (r.products ?? []).join(" | ");
    case "status":
      return r.status;
    case "employee":
      return r.employee;
    case "source":
      return r.source ?? "";
    case "priority":
      return r.priority ?? "";
  }
}

function escHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

/* ------------------------------ Main Component ---------------------------- */
export default function ViewLeads() {
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerAction, setPickerAction] = useState<"print" | "export">("export");
  const [selectedCols, setSelectedCols] = useState<Record<ExportableLeadColumn, boolean>>(
    () =>
      LEAD_COLUMNS.reduce((acc, c) => {
        acc[c.key] = true;
        return acc;
      }, {} as Record<ExportableLeadColumn, boolean>),
  );

  const {
    // table data
    pageRows,
    exportRows,
    serialStart,
    page,
    pageSize,
    start,
    totalFiltered,
    totalPages,

    // loading
    isLoading,
    isExportLoading,
    isError,
    deleteLoading,

    // filters & search
    filters,
    setFilters,
    globalQ,
    setGlobalQ,
    employeeOptions,
    statusCounts,

    // pagination
    setPage,

    // actions
    deleteLead,
  } = useLeadsPage();

  const handleEdit = (id: number | string) => {
    navigate(`/leads/${id}/edit`);
  };

  const handleDelete = (row: LeadRow) => {
    deleteLead(row.id);
  };

  const activeStatus: LeadStatus =
    (filters.status as LeadStatus | undefined) ?? "Pending";

  const handleStatusTabClick = (status: LeadStatus) => {
    setFilters((prev) => ({
      ...prev,
      status,
    }));
    setPage(1);
  };

  const handlePrint = () => {
    setPickerAction("print");
    setPickerOpen(true);
  };

  const handleExportCsv = () => {
    setPickerAction("export");
    setPickerOpen(true);
  };

  const chosenColumns = useMemo(
    () => LEAD_COLUMNS.filter((c) => selectedCols[c.key]),
    [selectedCols],
  );

  const applyPickerAction = () => {
    if (chosenColumns.length === 0) return;

    if (pickerAction === "export") {
      const rows = (exportRows ?? []).map((r) => {
        const out: Record<string, string | number> = {};
        for (const c of chosenColumns) {
          out[c.label] = getLeadValue(r, c.key);
        }
        return out;
      });
      downloadCsv(`leads-all-filtered.csv`, rows);
      setPickerOpen(false);
      return;
    }

    const rowsHtml = (exportRows ?? [])
      .map((r) => {
        const cells = chosenColumns
          .map((c) => `<td>${escHtml(getLeadValue(r, c.key))}</td>`)
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");
    const headerHtml = chosenColumns.map((c) => `<th>${escHtml(c.label)}</th>`).join("");

    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Leads Print</title>
  <style>
    @page { size: A4 landscape; margin: 10mm; }
    body { font-family: Arial, sans-serif; color: #111827; }
    h1 { font-size: 16px; margin: 0 0 8px; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    th, td { border: 1px solid #d1d5db; padding: 6px 8px; font-size: 11px; word-break: break-word; }
    th { background: #f3f4f6; text-align: left; }
  </style>
</head>
<body>
  <h1>Leads</h1>
  <table>
    <thead><tr>${headerHtml}</tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
</body>
</html>`;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
    setPickerOpen(false);
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="no-print mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Leads</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={globalQ}
              onChange={(e) => setGlobalQ(e.target.value)}
              placeholder="Search leads…"
              className="w-56 rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            />
          </div>

          {/* Print */}
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            onClick={handlePrint}
            title="Print (Landscape)"
          >
            <FiPrinter /> Print
          </button>

          {/* Export CSV (all filtered rows) */}
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            onClick={handleExportCsv}
            disabled={isExportLoading}
            title="Export all filtered rows to CSV"
          >
            <FiDownload /> {isExportLoading ? "Preparing…" : "Export"}
          </button>

          <button
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
            onClick={() => navigate("/leads/create")}
          >
            <FiPlus /> Add Lead
          </button>
        </div>
      </div>

      <TabBar
        ariaLabel="Lead status"
        className="no-print mb-4"
        activeKey={activeStatus}
        onSelect={(key) => handleStatusTabClick(key as LeadStatus)}
        items={STATUS_TABS.map((tab) => ({
          key: tab.key,
          label: tab.label,
          count: statusCounts?.[tab.key] ?? 0,
        }))}
      />

      {/* Print area: only table should be optimized for printing */}
      <div className="print-area">
        <LeadTable
          pageRows={pageRows}
          serialStart={serialStart}
          page={page}
          pageSize={pageSize}
          start={start}
          totalFiltered={totalFiltered}
          totalPages={totalPages}
          isLoading={isLoading}
          isError={isError}
          deleteLoading={deleteLoading}
          filters={filters}
          setFilters={setFilters}
          employeeOptions={employeeOptions}
          sourceOptions={SOURCE_OPTIONS}
          priorityOptions={PRIORITY_OPTIONS}
          setPage={setPage}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-xl bg-white p-4 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900">
              Select Columns ({pickerAction === "print" ? "Print" : "Export"})
            </h3>
            <p className="mt-1 text-xs text-gray-500">Choose columns to include. Default is all selected.</p>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {LEAD_COLUMNS.map((c) => (
                <label key={c.key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!selectedCols[c.key]}
                    onChange={(e) =>
                      setSelectedCols((prev) => ({ ...prev, [c.key]: e.target.checked }))
                    }
                  />
                  <span>{c.label}</span>
                </label>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => setPickerOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() =>
                  setSelectedCols(
                    LEAD_COLUMNS.reduce((acc, c) => {
                      acc[c.key] = true;
                      return acc;
                    }, {} as Record<ExportableLeadColumn, boolean>),
                  )
                }
              >
                Select All
              </button>
              <button
                type="button"
                className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
                disabled={chosenColumns.length === 0}
                onClick={applyPickerAction}
              >
                {pickerAction === "print" ? "Print" : "Export"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
